import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import CloseIcon from '@mui/icons-material/Close'
import BidQuestionsLeftPanel from './BidQuestionsLeftPanel'
import TabHeader from '../../comps/TabHeader'
import FancyProgressArc from '../../comps/FancyProgressArc'
import { FS_MICRO, FS_SM, FS_XS, ICON_LG, ICON_MD, ICON_SM } from '../../common/theme'
import { questionAnchorId, type Project, type Question } from '../../types'
import { QUESTION_PAGE_SIZE } from '../../comps/BidQuestion'

// Compact paginator — duplicated from BidScoreTab so each top-level
// tab can stand alone. Kept identical so layout stays consistent.
function CompactPaginator({ page, totalPages, total, onPage }: {
  page: number; totalPages: number; total: number; onPage: (p: number) => void
}) {
  if (total === 0) return null
  const start = (page - 1) * QUESTION_PAGE_SIZE + 1
  const end = Math.min(page * QUESTION_PAGE_SIZE, total)
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
      <Typography variant="caption" sx={{ fontSize: FS_MICRO, opacity: 0.6, whiteSpace: 'nowrap', mr: 0.5 }}>
        {start}–{end} of {total}
      </Typography>
      <IconButton size="small" disabled={page <= 1} onClick={() => onPage(1)} sx={{ p: 0.25 }}>
        <FirstPageIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <IconButton size="small" disabled={page <= 1} onClick={() => onPage(page - 1)} sx={{ p: 0.25 }}>
        <ChevronLeftIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <Typography variant="caption" sx={{ fontSize: FS_SM, mx: 0.25, whiteSpace: 'nowrap' }}>
        {page}/{totalPages}
      </Typography>
      <IconButton size="small" disabled={page >= totalPages} onClick={() => onPage(page + 1)} sx={{ p: 0.25 }}>
        <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
      <IconButton size="small" disabled={page >= totalPages} onClick={() => onPage(totalPages)} sx={{ p: 0.25 }}>
        <LastPageIcon sx={{ fontSize: ICON_SM }} />
      </IconButton>
    </Box>
  )
}

const ALL_KEY = '__all__'
const UNSECTIONED_KEY = '__unsectioned__'

interface BidWinThemesTabProps {
  project: Project
  allFilteredQuestions: Question[]
  pagedQuestions: Question[]
  totalQuestions: number
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  stateFilter?: string
  sectionFilter?: string
  onSectionChange?: (s: string | undefined) => void
  onBatchProcess?: (questions: Question[], options?: { genscore?: boolean; genimprovements?: boolean; genwintheme?: boolean }) => void
  onQuestionChange?: (q: Question) => void
}

// Win Themes top-level tab — extracted from the old BidScoreTab's
// `winthemes` sub-tab so the project nav can surface it alongside
// Overview / Questions / Score / Manage rather than buried as a
// secondary tab under Score.
export default function BidWinThemesTab({
  project, allFilteredQuestions, pagedQuestions, totalQuestions, currentPage, totalPages, onPageChange,
  stateFilter, sectionFilter, onSectionChange,
  onBatchProcess, onQuestionChange,
}: BidWinThemesTabProps) {
  console.log('BidWinThemesTab render')
  const winthemeScore = (project as any).projectbideval?.winthemescoreint ?? 0
  const winthemeElements: any[] = (project as any).projectbideval?.winthemeelements ?? []

  // ── Section tabs ─────────────────────────────────────────────────────
  const { sectionList, hasUnsectioned } = useMemo(() => {
    const seen = new Set<string>()
    const list: string[] = []
    let unsectioned = false
    for (const q of project.questions ?? []) {
      const sec = (q as any).section as string | null | undefined
      if (sec) {
        if (!seen.has(sec)) { seen.add(sec); list.push(sec) }
      } else {
        unsectioned = true
      }
    }
    return { sectionList: list, hasUnsectioned: unsectioned }
  }, [project.questions])

  const showSectionTabs = sectionList.length > 0

  const activeKey =
    sectionFilter === undefined ? ALL_KEY :
      sectionFilter === '' ? UNSECTIONED_KEY :
        sectionFilter

  const sectionTabs = [
    { key: ALL_KEY, label: 'All' },
    ...sectionList.map(s => ({ key: s, label: s })),
    ...(hasUnsectioned ? [{ key: UNSECTIONED_KEY, label: 'Unsectioned' }] : []),
  ]

  const handleSectionTabChange = (key: string) => {
    if (!onSectionChange) return
    if (key === ALL_KEY) onSectionChange(undefined)
    else if (key === UNSECTIONED_KEY) onSectionChange('')
    else onSectionChange(key)
  }

  // ── Left panel state ──────────────────────────────────────────────────
  const [leftPanelWidth, setLeftPanelWidth] = useState(280)
  const [leftCollapsed, setLeftCollapsed] = useState(false)
  const [selectedShas, setSelectedShas] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(280)

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientX - startXRef.current
      const maxW = (containerRef.current?.clientWidth ?? window.innerWidth) - 200
      setLeftPanelWidth(Math.max(180, Math.min(maxW, startWidthRef.current + delta)))
    }
    const onUp = () => {
      if (isResizingRef.current) {
        isResizingRef.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
      }
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    isResizingRef.current = true
    startXRef.current = e.clientX
    startWidthRef.current = leftPanelWidth
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  // Scroll the first question of the new page into view when the user
  // pages through the list.
  const isInitialPageRef = useRef(true)
  useEffect(() => {
    if (isInitialPageRef.current) {
      isInitialPageRef.current = false
      return
    }
    const firstSha = (pagedQuestions[0] as any)?.questionsha
    if (!firstSha) return
    const raf = requestAnimationFrame(() => {
      document.getElementById(questionAnchorId(firstSha))?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
    return () => cancelAnimationFrame(raf)
  }, [currentPage]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Batch actions ─────────────────────────────────────────────────────
  const batchSubject = (): Question[] => {
    if (selectedShas.size > 0) {
      return (project.questions ?? []).filter((q: any) => selectedShas.has(q.questionsha))
    }
    return allFilteredQuestions
  }

  const handleEvalWinThemes = () => {
    if (!onBatchProcess) return
    onBatchProcess(batchSubject(), { genwintheme: true })
  }

  return (
    <Box
      data-testid="bwt_root"
      ref={containerRef}
      sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}
    >

      {/* Left panel - question navigator */}
      {!leftCollapsed && (
        <Box sx={{
          flex: `0 0 ${leftPanelWidth}px`, minWidth: 0,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <BidQuestionsLeftPanel
            allQuestions={allFilteredQuestions}
            page={currentPage}
            onPage={onPageChange}
            onCollapse={() => setLeftCollapsed(true)}
            selectedShas={selectedShas}
            onSelectionChange={setSelectedShas}
            onQuestionChange={onQuestionChange}
            onBatchProcess={onBatchProcess ? (qs) => onBatchProcess(qs) : undefined}
          />
        </Box>
      )}

      {/* Resize handle / re-expand strip */}
      {leftCollapsed ? (
        <Box sx={{
          width: 18, flexShrink: 0,
          borderRight: 1, borderColor: 'divider',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          pt: 0.5, bgcolor: 'action.hover',
        }}>
          <Tooltip title="Show questions panel" placement="right">
            <IconButton
              data-testid="bwt_leftpanel_expand_btn"
              size="small"
              onClick={() => setLeftCollapsed(false)}
              sx={{ p: 0.25 }}
            >
              <ChevronRightIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box
          onMouseDown={handleResizeStart}
          sx={{
            width: 7, flexShrink: 0,
            cursor: 'col-resize',
            borderLeft: 1, borderRight: 1, borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <MoreVertIcon sx={{ fontSize: ICON_LG, opacity: 0.35 }} />
        </Box>
      )}

      {/* Right side */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

        {/* Win themes header */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, gap: 1.25, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.7, fontSize: FS_XS }}>
            Overall Win Theme Score
          </Typography>
          <FancyProgressArc value={winthemeScore} zerona scale={0.85} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, ml: 1 }}>
            <Tooltip title="Add Win Themes">
              <IconButton data-testid="bwt_add_winthemes_btn" size="small">
                <LayersOutlinedIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </Tooltip>
            {winthemeElements.length === 0 && (
              <Typography variant="caption" sx={{ opacity: 0.5, fontSize: FS_XS, alignSelf: 'center' }}>
                No win themes added yet
              </Typography>
            )}
            {winthemeElements.map((e: any, i: number) => (
              <Box key={i} sx={{
                display: 'flex', alignItems: 'center', gap: 0.5,
                px: 0.75, py: 0.25, borderRadius: 1,
                bgcolor: 'action.selected', fontSize: FS_XS,
              }}>
                <LayersOutlinedIcon sx={{ fontSize: 14 }} />
                <Typography variant="caption" sx={{ fontSize: FS_XS }}>{e.name}</Typography>
                <CloseIcon sx={{ fontSize: 12, opacity: 0.6, cursor: 'pointer', '&:hover': { opacity: 1 } }} />
              </Box>
            ))}
          </Box>
          <Tooltip title={selectedShas.size > 0 ? `Evaluate Win Themes (${selectedShas.size} selected)` : 'Evaluate Win Themes (all)'}>
            <IconButton data-testid="bwt_eval_winthemes_btn" size="small" onClick={handleEvalWinThemes}>
              <EmojiEventsOutlinedIcon sx={{ fontSize: ICON_MD }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Section tabs + paginator */}
        {showSectionTabs ? (
          <TabHeader
            tabs={sectionTabs}
            active={activeKey}
            onChange={handleSectionTabChange}
            endSlot={
              <CompactPaginator
                page={currentPage}
                totalPages={totalPages}
                total={totalQuestions}
                onPage={onPageChange}
              />
            }
            testIdPrefix="bidwinthemessection"
          />
        ) : (
          totalQuestions > 0 && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
              px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0,
            }}>
              <CompactPaginator
                page={currentPage}
                totalPages={totalPages}
                total={totalQuestions}
                onPage={onPageChange}
              />
            </Box>
          )
        )}

        {/* Per-question win-theme checkpoints */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
          {pagedQuestions.length === 0 ? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.45 }}>
                {stateFilter ? 'No questions match this filter.' : 'No questions found.'}
              </Typography>
            </Box>
          ) : (
            pagedQuestions.map((q: any, i: number) => {
              const checkpoints: any[] = q.winthemeeval?.winthemecheckpoints ?? []
              return (
                <Box
                  key={q.questionsha ?? i}
                  sx={{ mb: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: FS_XS }}>
                      {q.numbering ?? `Q${(currentPage - 1) * QUESTION_PAGE_SIZE + i + 1}`}
                    </Typography>
                    <Typography variant="caption" sx={{ flex: 1, fontSize: FS_XS, opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      dangerouslySetInnerHTML={{ __html: q.question ?? '' }}
                    />
                  </Box>
                  {checkpoints.length === 0 ? (
                    <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.5, fontStyle: 'italic' }}>
                      Not yet evaluated
                    </Typography>
                  ) : (
                    checkpoints.map((cp: any, ci: number) => (
                      <Box key={ci} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5, borderTop: ci === 0 ? 0 : 1, borderColor: 'divider' }}>
                        <Box sx={{ flexShrink: 0, mt: 0.25 }}>
                          {((cp.checkstatestring === 'auto' && cp.relevant) || cp.checkstatestring === 'include')
                            ? (cp.passed
                              ? <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, fontSize: FS_MICRO }}>PASSED</Typography>
                              : <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, fontSize: FS_MICRO }}>FAILED</Typography>)
                            : <Typography variant="caption" sx={{ opacity: 0.55, fontWeight: 600, fontSize: FS_MICRO }}>N/A</Typography>}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: FS_XS, lineHeight: 1.4 }}>
                            {cp.checkpoint}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: FS_MICRO, opacity: 0.7, mt: 0.25, lineHeight: 1.4 }}>
                            {((cp.checkstatestring === 'auto' && cp.relevant) || cp.checkstatestring === 'include')
                              ? cp.reason
                              : cp.reasonnotrelevant}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              )
            })
          )}
        </Box>
      </Box>
    </Box>
  )
}
